import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'About', path: '/about' },
    { name: 'Blog', path: '/blog' },
    { name: 'Consult', path: '/consult' },
  ];

  return (
    <>
      <motion.nav
        initial={isHome ? { y: -20, opacity: 0 } : { y: 0, opacity: 1 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: isHome ? 2.5 : 0, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          isScrolled
            ? 'bg-white/90 backdrop-blur-md shadow-[0_1px_0_rgba(0,0,0,0.05)]'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="block">
              <img 
                src="/assets/logo_full_dark.svg" 
                alt="Grapix" 
                className="h-8 w-auto" 
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm transition-colors duration-200 ${
                    location.pathname === link.path
                      ? 'text-[#1a1a1a] font-medium'
                      : 'text-[#5f6368] hover:text-[#1a1a1a]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden md:flex items-center">
              <Link
                to="/consult"
                className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white text-sm px-5 py-2 rounded-full hover:bg-[#333] transition-colors duration-200"
              >
                Get in Touch
                <ArrowRight size={14} />
              </Link>
            </div>

            {/* Mobile Toggle */}
            <button
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className="md:hidden p-2 text-[#1a1a1a]"
            >
              {isMobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 bg-white transition-transform duration-300 md:hidden ${
          isMobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ paddingTop: '64px' }}
      >
        <div className="px-6 py-8 flex flex-col gap-2">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-2xl py-3 border-b border-[#f0f0f0] transition-colors ${
                location.pathname === link.path
                  ? 'text-[#1a1a1a] font-medium'
                  : 'text-[#5f6368]'
              }`}
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              {link.name}
            </Link>
          ))}
          <Link
            to="/consult"
            className="mt-6 inline-flex items-center justify-center gap-2 bg-[#1a1a1a] text-white text-base px-6 py-3 rounded-full"
          >
            Get in Touch
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </>
  );
};

export default Navbar;
